// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Select from 'react-select';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Timezones } from '@/utils'
import { useState } from 'react';
import { formatDatePreview, formatTimePreview } from '@/hooks/DateTimePreview';
import { KeenIcon } from '@/components';
import { useAuthContext } from '@/auth';
import apiService from '@/services/apiService';
import profileService from '@/services/setting/profileService';
import { getGlobalSelectClass, isSuperAdmin } from '@/utils';

/** Same default as the backend's FirmClock when a node has no setting yet. */
const DEFAULT_FIRM_TIMEZONE = 'America/Los_Angeles';

const AdvancedSettingsPreferences = ({ data, onConfirm, loadData, loadDataSave }) => {
  const { auth, updateUser } = useAuthContext();
  const [isEditing, setIsEditing] = useState(false);
  const Currency = [
    { value: 'United States Dollar (USD)', label: 'United States Dollar (USD)', isDisabled: false },
    { value: 'Japanese Yen (JPY)', label: 'Japanese Yen (JPY)', isDisabled: true },
    { value: 'British Pound Sterling (GBP)', label: 'British Pound Sterling (GBP)', isDisabled: true },
  ];
  const formattedTimezones = Timezones.map(tz => ({
    label: tz.replace(/_/g, ' '), // Optional: make label more readable
    value: tz
  }));
  const formFields = {
    date_format: {
      label: 'Date Format',
      type: 'select',
      options: [
        { label: 'YYYY-MM-DD (2025-04-16)', value: 'Y-m-d' },
        { label: 'DD-MM-YYYY (16-04-2025)', value: 'd-m-Y' },
        { label: 'MM-DD-YYYY (04-16-2025)', value: 'm-d-Y' },
        // { label: 'DD-MMM-YYYY (16-Apr-2025)', value: 'd-M-Y' },
        { label: 'D-M-YYYY (16-4-2025)', value: 'j-n-Y' },
        { label: 'YYYY/MM/DD (2025/04/16)', value: 'Y/m/d' },
        { label: 'DD/MM/YYYY (16/04/2025)', value: 'd/m/Y' },
        { label: 'MM/DD/YYYY (04/16/2025)', value: 'm/d/Y' },
        // { label: 'DD/MMM/YYYY (16/Apr/2025)', value: 'd/M/Y' },
        { label: 'D/M/YYYY (16/4/2025)', value: 'j/n/Y' },
      ],
    },
    time_format: {
      label: 'Time Format',
      type: 'custom-time-format',
    },
    // The firm's zone (OL-210): task due times, overdue and the 7:00 a.m.
    // digest are worked out in it. Not shown on the super admin's settings.
    ...(isSuperAdmin() ? {} : {
      timezone: {
        label: 'Firm Time Zone',
        type: 'select',
        options: formattedTimezones,
      },
    }),
    currency: {
      label: 'Currency',
      type: 'select',
      options: [
        { value: 'United States Dollar (USD)', label: 'United States Dollar (USD)', isDisabled: false },
        { value: 'Japanese Yen (JPY)', label: 'Japanese Yen (JPY)', isDisabled: true },
        { value: 'British Pound Sterling (GBP)', label: 'British Pound Sterling (GBP)', isDisabled: true },
      ],
    },
    // The signed-in user's own profile zone, saved through the profile endpoint.
    timeZone: {
      label: 'Your Time Zone',
      type: 'select',
      options: formattedTimezones
    }
  };

  const validationSchema = Yup.object().shape({
    date_format: Yup.string(),
    time_format: Yup.string(),
  });

  const initialValues = {
    account_owner: data?.account_owner || '',
    legal_business_name: data?.legal_business_name || '',
    country: data?.country || '',
    address: data?.address || '',
    address_2: data?.address_2 || '',
    city: data?.city || '',
    zip_postal_code: data?.zip_postal_code || '',
    phone_number: data?.phone_number || '',
    firm_email: data?.firm_email || '',
    website: data?.website || '',
    tax_id: data?.tax_id || '',
    date_format: data?.date_format || '',
    time_format: data?.time_format || '',
    ...(isSuperAdmin() ? {} : { timezone: data?.timezone || DEFAULT_FIRM_TIMEZONE }),
    // company_logo: data?.company_logo ? [{ dataURL: data?.company_logo }] : []
    company_logo: data?.company_logo ? data?.company_logo : [],
    currency: Currency[0].value,
    timeZone: auth.user.timezone
  }
  // console.log('initialValues', initialValues, Currency, auth)
  const handleCancel = (resetForm) => {
    setIsEditing(false)
    resetForm()
  }

  const handleTimezoneUpdate = async (newTimezone) => {
    try {
      const allowedFields = [
        'first_name',
        'last_name',
        'email',
        'profile_image',
        'address',
        'timezone'
      ];

      const payload = {};

      allowedFields.forEach((field) => {
        if (field === 'address') {
          // ensure address goes even if empty
          payload.address = auth.user.address ?? {};
        } else {
          payload[field] = auth.user[field] ?? '';
        }
      });

      // override timezone
      payload.timezone = newTimezone;

      const formData = new FormData();

      Object.entries(payload).forEach(([key, value]) => {

        if (key === 'address') {
          // 🔥 FORCE append nested address fields always
          const addressObj = value || {};

          formData.append("address[address]", addressObj.address ?? "");
          formData.append("address[city]", addressObj.city ?? "");
          formData.append("address[state]", addressObj.state ?? "");
          formData.append("address[zip]", addressObj.zip ?? "");

        } else {
          formData.append(key, value ?? '');
        }
      });

      formData.append("_method", "PUT");

      const { data, status } = await profileService.updateProfile(formData);

      if (status) {
        updateUser(data.user, true);
      }

      console.log("Timezone updated successfully", data);

    } catch (error) {
      console.error("Failed to update timezone", error);
    }
  };



  return (

    <div className="card">
      <div className="card-header" id="advanced_settings_preferences">
        <h3 className="card-title">Preferences</h3>
        <button onClick={() => setIsEditing(true)} className="btn btn-xs btn-icon btn-icon-lg link">
          <KeenIcon icon="notepad-edit" className="text-[12px]" />
        </button>
      </div>
      <div className="card-body grid gap-5 lg:py-7.5">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={(values) => {
            const { timeZone, currency, ...filteredValues } = values;
            const formData = new FormData();
            const avatarFile = Array.isArray(values.company_logo) && values.company_logo.length > 0
              ? values.company_logo[0].file
              : values.company_logo;
            // const finalData = {
            //   ...initialValues,   // pre-fill everything
            //   ...values,        // override only what was edited in the form
            //   company_logo: avatarFile,
            // };
            const finalData = {
              ...filteredValues,
              company_logo: avatarFile,
            };
            Object.entries(finalData).forEach(([key, value]) => {
              formData.append(key, value ?? '');
            });

            formData.append('_method', 'PUT');
            // for (let pair of formData.entries()) {
            //   console.log(pair[0] + ': ' + pair[1]);
            // }
            // return
            onConfirm(formData);
            if (values.timeZone && values.timeZone !== auth.user.timezone) {
              handleTimezoneUpdate(values.timeZone);
            }
            setIsEditing(false);
          }}
        >
          {({ values, setFieldValue, resetForm }) => (
            <Form>
              <table className="table text-sm text-gray-500 align-middle">
                <tbody>
                  {loadData || loadDataSave ? (
                    <tr>
                      <td colSpan={2} className="py-4 text-center">
                        <span>Loading...</span>
                      </td>
                    </tr>
                  ) : (
                    <>
                      {Object.entries(formFields).map(([key, config]) => (
                        <tr key={key}>
                          <td className="py-2 text-gray-600">{config.label}</td>
                          <td className="py-2 text-gray-800">
                            {isEditing ? (
                              key === 'time_format' ? (
                                <div>
                                  <div className="flex gap-4 mb-2">
                                    <label className="flex items-center gap-1">
                                      <Field
                                        type="radio"
                                        name="time_format_group"
                                        value="24"
                                        checked={['H:i', 'H:i:s', 'H.i', 'H.i.s'].includes(values.time_format)}
                                        onChange={() => {
                                          setFieldValue('time_format', 'H:i');
                                        }}
                                      />
                                      24-Hour
                                    </label>
                                    <label className="flex items-center gap-1">
                                      <Field
                                        type="radio"
                                        name="time_format_group"
                                        value="12"
                                        checked={['h:i A', 'h:i:s A', 'h:i a', 'h:i:s a'].includes(values.time_format)}
                                        onChange={() => {
                                          setFieldValue('time_format', 'h:i A');
                                        }}
                                      />
                                      12-Hour
                                    </label>
                                  </div>
                                  <Field as="select" name="time_format" className="w-full cursor-pointer select input-bordered">
                                    {['H:i', 'H:i:s', 'H.i', 'H.i.s'].includes(values.time_format) && (
                                      <>
                                        <option value="H:i">24-Hour: HH:MM (14:30)</option>
                                        <option value="H:i:s">24-Hour: HH:MM:SS (14:30:00)</option>
                                        <option value="H.i">24-Hour: HH.MM (14.30)</option>
                                        <option value="H.i.s">24-Hour: HH.MM.SS (14.30.00)</option>
                                      </>
                                    )}
                                    {['h:i A', 'h:i:s A', 'h:i a', 'h:i:s a'].includes(values.time_format) && (
                                      <>
                                        <option value="h:i A">12-Hour: HH:MM AM/PM (02:30 PM)</option>
                                        <option value="h:i:s A">12-Hour: HH:MM:SS AM/PM (02:30:00 PM)</option>
                                        <option value="h:i a">12-Hour: HH:MM am/pm (02:30 pm)</option>
                                        <option value="h:i:s a">12-Hour: HH:MM:SS am/pm (02:30:00 pm)</option>
                                      </>
                                    )}
                                  </Field>
                                </div>
                              ) : key === 'date_format' ? (
                                <Field as="select" name="date_format" className="w-full select cursor-pointer input-bordered">
                                  {config.options.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </Field>
                              ) : key === 'currency' ? (
                                <Select
                                  name="currency"
                                  options={config.options}
                                  value={config.options.find(option => option.value === values.currency)}
                                  onChange={selected => setFieldValue('currency', selected.value)}
                                  isSearchable
                                  isOptionDisabled={(option) => option.isDisabled}
                                  classNames={getGlobalSelectClass()}
                                  className="w-full p-0 input react-select-container"
                                  classNamePrefix="react-select"
                                />
                              ) : key === 'timeZone' || key === 'timezone' ? (
                                <Select
                                  name={key}
                                  options={config.options}
                                  value={config.options.find(option => option.value === values[key])}
                                  onChange={selected => setFieldValue(key, selected.value)}
                                  isSearchable
                                  classNames={getGlobalSelectClass()}
                                  className="w-full p-0 input react-select-container"
                                  classNamePrefix="react-select"
                                />
                              ) : (
                                <>
                                  <Field name={key} className="w-full input input-sm input-bordered" />
                                  <ErrorMessage name={key} component="div" className="mt-1 text-xs text-red-500" />
                                </>
                              )
                            ) : (
                              key === 'date_format'
                                ? formatDatePreview(values[key])
                                : key === 'time_format'
                                  ? formatTimePreview(values[key])
                                  // : key === 'currency'
                                  // ? 
                                  : values[key] || '—'
                            )}
                          </td>
                        </tr>
                      ))}
                      {isEditing && (
                        <tr>
                          <td></td>
                          <td className="pt-3">
                            <button
                              type="submit"
                              disabled={loadDataSave}
                              className="btn btn-sm btn-primary"
                            >
                              {loadDataSave ? "Saving..." : "Save"}
                            </button>
                            <button
                              type="button"
                              onClick={() => { handleCancel(resetForm) }}
                              className="btn btn-sm "
                            >
                              Cancel
                            </button>
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  )
};
export { AdvancedSettingsPreferences };